// Category hierarchy with Lucide icons
import {
  Laptop, Monitor, Smartphone, Headphones, Gamepad2, Camera, Tv,
  Shirt, User, Footprints, Watch, UserCircle2, Baby,
  Home, Armchair, UtensilsCrossed, Lamp, BedDouble, TreePine,
  Bike, Dumbbell, Tent, Trophy,
  Book, BookOpen, Film, Music,
  Puzzle, Dices, Box, GraduationCap,
  Sparkles, Scissors,
  MoreHorizontal, HardDrive, Wrench, Snowflake, Waves, Radio, Plug, Package,
  // New icons for expanded categories
  Car, Plane, Hotel, MapPin,
  Heart, Pill, Stethoscope, Activity,
  Coffee, Wine, Cookie,
  Dog, Cat, Fish,
  Paintbrush, Palette, Hammer,
  Briefcase, PenTool, Calculator,
  Gem, Crown, Gift,
  ShoppingBag, CreditCard
} from 'lucide-react';

const categories = {
  electronics: {
    label: { en: 'Electronics', no: 'Elektronikk' },
    icon: Laptop,
    subcategories: {
      computers: { label: { en: 'Computers & Laptops', no: 'Datamaskiner & Bærbare' }, icon: Monitor },
      phones: { label: { en: 'Phones & Tablets', no: 'Telefoner & Nettbrett' }, icon: Smartphone },
      tvs: { label: { en: 'TVs & Displays', no: 'TV-er & Skjermer' }, icon: Tv },
      audio: { label: { en: 'Audio & Speakers', no: 'Lyd & Høyttalere' }, icon: Headphones },
      gaming: { label: { en: 'Gaming', no: 'Gaming' }, icon: Gamepad2 },
      cameras: { label: { en: 'Cameras', no: 'Kameraer' }, icon: Camera },
      storage: { label: { en: 'Storage & Drives', no: 'Lagring & Disker' }, icon: HardDrive },
      accessories: { label: { en: 'Electronics Accessories', no: 'Elektronikk Tilbehør' }, icon: Plug },
      smart: { label: { en: 'Smart Home', no: 'Smarthjem' }, icon: Home },
      wearables: { label: { en: 'Wearables', no: 'Bærbare enheter' }, icon: Watch }
    }
  },
  clothing: {
    label: { en: 'Clothing', no: 'Klær' },
    icon: Shirt,
    subcategories: {
      men: {
        label: { en: 'Men', no: 'Menn' },
        icon: User,
        subcategories: {
          tops: { label: { en: 'Tops', no: 'Topper' }, icon: Shirt },
          bottoms: { label: { en: 'Bottoms', no: 'Bukser' }, icon: Package },
          shoes: { label: { en: 'Shoes', no: 'Sko' }, icon: Footprints },
          accessories: { label: { en: 'Accessories', no: 'Tilbehør' }, icon: Watch }
        }
      },
      women: {
        label: { en: 'Women', no: 'Kvinner' },
        icon: UserCircle2,
        subcategories: {
          tops: { label: { en: 'Tops', no: 'Topper' }, icon: Shirt },
          bottoms: { label: { en: 'Bottoms', no: 'Bukser' }, icon: Package },
          shoes: { label: { en: 'Shoes', no: 'Sko' }, icon: Footprints },
          accessories: { label: { en: 'Accessories', no: 'Tilbehør' }, icon: Watch }
        }
      },
      kids: {
        label: { en: 'Kids', no: 'Barn' },
        icon: Baby,
        subcategories: {
          tops: { label: { en: 'Tops', no: 'Topper' }, icon: Shirt },
          bottoms: { label: { en: 'Bottoms', no: 'Bukser' }, icon: Package },
          shoes: { label: { en: 'Shoes', no: 'Sko' }, icon: Footprints },
          accessories: { label: { en: 'Accessories', no: 'Tilbehør' }, icon: Watch }
        }
      }
    }
  },
  home: {
    label: { en: 'Home & Living', no: 'Hjem & Bolig' },
    icon: Home,
    subcategories: {
      furniture: { label: { en: 'Furniture', no: 'Møbler' }, icon: Armchair },
      kitchen: { label: { en: 'Kitchen', no: 'Kjøkken' }, icon: UtensilsCrossed },
      appliances: { label: { en: 'Appliances', no: 'Hvitevarer' }, icon: Radio },
      decor: { label: { en: 'Decor', no: 'Innredning' }, icon: Lamp },
      bedding: { label: { en: 'Bedding', no: 'Sengetøy' }, icon: BedDouble },
      garden: { label: { en: 'Garden', no: 'Hage' }, icon: TreePine },
      tools: { label: { en: 'Tools & Hardware', no: 'Verktøy & Maskindeler' }, icon: Wrench },
      storage: { label: { en: 'Storage & Organization', no: 'Oppbevaring & Organisering' }, icon: Box },
      cleaning: { label: { en: 'Cleaning Supplies', no: 'Rengjøring' }, icon: Package },
      bathroom: { label: { en: 'Bathroom', no: 'Bad' }, icon: Home }
    }
  },
  sports: {
    label: { en: 'Sports & Outdoors', no: 'Sport & Friluft' },
    icon: Bike,
    subcategories: {
      fitness: { label: { en: 'Fitness', no: 'Trening' }, icon: Dumbbell },
      outdoor: { label: { en: 'Outdoor Gear', no: 'Friluftsutstyr' }, icon: Tent },
      equipment: { label: { en: 'Sports Equipment', no: 'Sportsutstyr' }, icon: Trophy },
      cycling: { label: { en: 'Cycling & Bikes', no: 'Sykkel & Sykling' }, icon: Bike },
      watersports: { label: { en: 'Water Sports', no: 'Vannsport' }, icon: Waves },
      wintersports: { label: { en: 'Winter Sports', no: 'Vintersport' }, icon: Snowflake },
      running: { label: { en: 'Running', no: 'Løping' }, icon: Footprints },
      team: { label: { en: 'Team Sports', no: 'Lagsport' }, icon: Trophy }
    }
  },
  media: {
    label: { en: 'Books & Media', no: 'Bøker & Media' },
    icon: Book,
    subcategories: {
      books: { label: { en: 'Books', no: 'Bøker' }, icon: BookOpen },
      movies: { label: { en: 'Movies & TV', no: 'Filmer & TV' }, icon: Film },
      music: { label: { en: 'Music', no: 'Musikk' }, icon: Music },
      audiobooks: { label: { en: 'Audiobooks', no: 'Lydbøker' }, icon: Headphones },
      magazines: { label: { en: 'Magazines', no: 'Tidsskrifter' }, icon: Book },
      streaming: { label: { en: 'Streaming Services', no: 'Strømmetjenester' }, icon: Tv }
    }
  },
  toys: {
    label: { en: 'Toys & Games', no: 'Leker & Spill' },
    icon: Puzzle,
    subcategories: {
      board: { label: { en: 'Board Games', no: 'Brettspill' }, icon: Dices },
      building: { label: { en: 'Building Sets', no: 'Byggekasser' }, icon: Box },
      educational: { label: { en: 'Educational', no: 'Pedagogisk' }, icon: GraduationCap },
      action: { label: { en: 'Action Figures', no: 'Actionfigurer' }, icon: User },
      dolls: { label: { en: 'Dolls & Playsets', no: 'Dukker & Lekesett' }, icon: Baby },
      puzzles: { label: { en: 'Puzzles', no: 'Puslespill' }, icon: Puzzle },
      outdoor: { label: { en: 'Outdoor Toys', no: 'Utendørsleker' }, icon: Bike }
    }
  },
  beauty: {
    label: { en: 'Beauty & Personal Care', no: 'Skjønnhet & Personlig Pleie' },
    icon: Sparkles,
    subcategories: {
      skincare: { label: { en: 'Skincare', no: 'Hudpleie' }, icon: Sparkles },
      haircare: { label: { en: 'Haircare', no: 'Hårpleie' }, icon: Scissors },
      makeup: { label: { en: 'Makeup', no: 'Sminke' }, icon: Sparkles },
      fragrance: { label: { en: 'Fragrance', no: 'Parfyme' }, icon: Sparkles },
      grooming: { label: { en: 'Grooming', no: 'Styling' }, icon: Scissors },
      wellness: { label: { en: 'Wellness Products', no: 'Velværeprodukter' }, icon: Heart }
    }
  },
  food: {
    label: { en: 'Food & Beverages', no: 'Mat & Drikke' },
    icon: UtensilsCrossed,
    subcategories: {
      coffee: { label: { en: 'Coffee & Tea', no: 'Kaffe & Te' }, icon: Coffee },
      wine: { label: { en: 'Wine & Spirits', no: 'Vin & Brennevin' }, icon: Wine },
      snacks: { label: { en: 'Snacks & Treats', no: 'Snacks & Godteri' }, icon: Cookie },
      cooking: { label: { en: 'Cooking Ingredients', no: 'Matlaging' }, icon: UtensilsCrossed },
      giftcards: { label: { en: 'Restaurant Gift Cards', no: 'Restaurantgavekort' }, icon: Gift }
    }
  },
  health: {
    label: { en: 'Health & Wellness', no: 'Helse & Velvære' },
    icon: Heart,
    subcategories: {
      fitness: { label: { en: 'Fitness Equipment', no: 'Treningsutstyr' }, icon: Dumbbell },
      supplements: { label: { en: 'Supplements', no: 'Kosttilskudd' }, icon: Pill },
      medical: { label: { en: 'Medical & Health', no: 'Medisinsk & Helse' }, icon: Stethoscope },
      wellness: { label: { en: 'Wellness & Spa', no: 'Velvære & Spa' }, icon: Heart },
      yoga: { label: { en: 'Yoga & Meditation', no: 'Yoga & Meditasjon' }, icon: Activity }
    }
  },
  automotive: {
    label: { en: 'Automotive', no: 'Bil & Motor' },
    icon: Car,
    subcategories: {
      accessories: { label: { en: 'Car Accessories', no: 'Biltilbehør' }, icon: Car },
      parts: { label: { en: 'Parts & Maintenance', no: 'Deler & Vedlikehold' }, icon: Wrench },
      electronics: { label: { en: 'Car Electronics', no: 'Bilelektronikk' }, icon: Radio }
    }
  },
  travel: {
    label: { en: 'Travel & Experiences', no: 'Reise & Opplevelser' },
    icon: Plane,
    subcategories: {
      luggage: { label: { en: 'Luggage & Bags', no: 'Bagasje & Vesker' }, icon: ShoppingBag },
      accessories: { label: { en: 'Travel Accessories', no: 'Reisetilbehør' }, icon: MapPin },
      experiences: { label: { en: 'Experiences', no: 'Opplevelser' }, icon: Gift },
      giftcards: { label: { en: 'Travel Gift Cards', no: 'Reisegavekort' }, icon: Gift },
      hotels: { label: { en: 'Hotels & Accommodation', no: 'Hoteller & Overnatting' }, icon: Hotel }
    }
  },
  pets: {
    label: { en: 'Pets & Animals', no: 'Dyr & Kjæledyr' },
    icon: Dog,
    subcategories: {
      dogs: { label: { en: 'Dogs', no: 'Hunder' }, icon: Dog },
      cats: { label: { en: 'Cats', no: 'Katter' }, icon: Cat },
      fish: { label: { en: 'Fish & Aquarium', no: 'Fisk & Akvarium' }, icon: Fish },
      supplies: { label: { en: 'Pet Supplies', no: 'Dyreutstyr' }, icon: Package },
      toys: { label: { en: 'Pet Toys', no: 'Dyreleker' }, icon: Puzzle }
    }
  },
  hobbies: {
    label: { en: 'Hobbies & Crafts', no: 'Hobbyer & Håndverk' },
    icon: Paintbrush,
    subcategories: {
      art: { label: { en: 'Art Supplies', no: 'Kunstsaker' }, icon: Palette },
      painting: { label: { en: 'Painting', no: 'Maling' }, icon: Paintbrush },
      crafting: { label: { en: 'Crafting', no: 'Håndverk' }, icon: Scissors },
      tools: { label: { en: 'Craft Tools', no: 'Håndverksverktøy' }, icon: Hammer },
      sewing: { label: { en: 'Sewing & Textiles', no: 'Søm & Tekstiler' }, icon: Scissors }
    }
  },
  office: {
    label: { en: 'Office & School', no: 'Kontor & Skole' },
    icon: Briefcase,
    subcategories: {
      supplies: { label: { en: 'Office Supplies', no: 'Kontorartikler' }, icon: PenTool },
      school: { label: { en: 'School Supplies', no: 'Skoleartikler' }, icon: BookOpen },
      stationery: { label: { en: 'Stationery', no: 'Skrivesaker' }, icon: PenTool },
      tech: { label: { en: 'Office Tech', no: 'Kontorteknologi' }, icon: Calculator }
    }
  },
  jewelry: {
    label: { en: 'Jewelry & Accessories', no: 'Smykker & Tilbehør' },
    icon: Gem,
    subcategories: {
      jewelry: { label: { en: 'Jewelry', no: 'Smykker' }, icon: Gem },
      watches: { label: { en: 'Watches', no: 'Klokker' }, icon: Watch },
      accessories: { label: { en: 'Accessories', no: 'Tilbehør' }, icon: Crown }
    }
  },
  baby: {
    label: { en: 'Baby & Kids', no: 'Baby & Barn' },
    icon: Baby,
    subcategories: {
      clothing: { label: { en: 'Baby Clothing', no: 'Babyklær' }, icon: Shirt },
      gear: { label: { en: 'Baby Gear', no: 'Babyutstyr' }, icon: Package },
      toys: { label: { en: 'Baby Toys', no: 'Babyleker' }, icon: Puzzle },
      care: { label: { en: 'Baby Care', no: 'Babypleie' }, icon: Baby },
      furniture: { label: { en: 'Nursery Furniture', no: 'Barnerom' }, icon: BedDouble }
    }
  },
  gifts: {
    label: { en: 'Gift Cards & Experiences', no: 'Gavekort & Opplevelser' },
    icon: Gift,
    subcategories: {
      giftcards: { label: { en: 'Gift Cards', no: 'Gavekort' }, icon: Gift },
      experiences: { label: { en: 'Experiences', no: 'Opplevelser' }, icon: Gift },
      subscriptions: { label: { en: 'Subscriptions', no: 'Abonnementer' }, icon: CreditCard }
    }
  },
  other: {
    label: { en: 'Other', no: 'Annet' },
    icon: MoreHorizontal
  }
};

/**
 * Get the current language from localStorage or default to 'en'
 */
const getCurrentLanguage = () => {
  const lang = localStorage.getItem('language') || 'en';
  return lang === 'no' ? 'no' : 'en';
};

/**
 * Get all categories
 */
export const getAllCategories = () => {
  return categories;
};

/**
 * Get category by path
 * @param {string} path - e.g., "clothing.men.shoes"
 */
export const getCategoryByPath = (path) => {
  if (!path) return null;
  
  const parts = path.split('.');
  let current = categories;
  
  for (const part of parts) {
    if (current[part]) {
      current = current[part];
    } else if (current.subcategories && current.subcategories[part]) {
      current = current.subcategories[part];
    } else {
      return null;
    }
  }
  
  return current;
};

/**
 * Get category icon component
 * @param {string} path - e.g., "clothing.men.shoes"
 */
export const getCategoryIcon = (path) => {
  const category = getCategoryByPath(path);
  if (category && category.icon) {
    return category.icon;
  }
  return MoreHorizontal;
};

/**
 * Get category label
 * @param {string} path - e.g., "clothing.men.shoes"
 */
export const getCategoryLabel = (path) => {
  if (!path) return 'Other';
  
  const category = getCategoryByPath(path);
  if (category && category.label) {
    const lang = getCurrentLanguage();
    return typeof category.label === 'string' ? category.label : category.label[lang];
  }
  return 'Other';
};

/**
 * Get full path label (e.g., "Clothing > Men > Shoes")
 */
export const getCategoryPathLabel = (path) => {
  if (!path) return 'Other';
  
  const parts = path.split('.');
  const labels = [];
  let current = categories;
  const lang = getCurrentLanguage();
  
  for (const part of parts) {
    if (current[part]) {
      current = current[part];
      if (current.label) {
        const label = typeof current.label === 'string' ? current.label : current.label[lang];
        labels.push(label);
      }
    } else if (current.subcategories && current.subcategories[part]) {
      current = current.subcategories[part];
      if (current.label) {
        const label = typeof current.label === 'string' ? current.label : current.label[lang];
        labels.push(label);
      }
    }
  }
  
  return labels.join(' > ') || 'Other';
};

/**
 * Search categories
 * @param {string} query - Search query
 */
export const searchCategories = (query) => {
  if (!query || query.trim().length === 0) {
    return [];
  }
  
  const results = [];
  const searchTerm = query.toLowerCase();
  const lang = getCurrentLanguage();
  
  const searchRecursive = (obj, path = '', depth = 0) => {
    for (const key in obj) {
      if (key === 'icon') continue;
      
      const item = obj[key];
      const currentPath = path ? `${path}.${key}` : key;
      
      if (item.label) {
        const label = typeof item.label === 'string' ? item.label : item.label[lang];
        if (label.toLowerCase().includes(searchTerm)) {
          results.push({
            path: currentPath,
            label: label,
            icon: item.icon,
            depth
          });
        }
      }
      
      if (item.subcategories) {
        searchRecursive(item.subcategories, currentPath, depth + 1);
      }
    }
  };
  
  searchRecursive(categories);
  return results;
};
